import { SITE } from "@/config/site";

function LegalShell({ title, updated, children, testId }) {
  return (
    <div className="min-h-screen bg-[#080705] text-[#F7F4EF]">
      <header className="border-b border-[#D4AF37]/15">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-4 sm:px-8">
          <a href="/" data-testid="legal-home-link" className="flex items-center">
            <img
              src={SITE.brand.logoSmall}
              alt={SITE.brand.logoAlt}
              className="h-12 w-auto"
            />
          </a>
          <a
            href="/"
            data-testid="legal-back-link"
            className="rounded-full border border-[#D4AF37]/30 px-5 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#F3E5AB] transition-colors hover:bg-[#D4AF37]/10"
          >
            Back to Site
          </a>
        </div>
      </header>
      <main data-testid={testId} className="mx-auto max-w-3xl px-5 py-14 sm:px-8 sm:py-20">
        <h1 className="font-display gold-text text-4xl sm:text-5xl">{title}</h1>
        <p className="mt-3 text-sm text-[#918A7E]">Last updated: {updated}</p>
        <div className="mt-10 space-y-9">{children}</div>
      </main>
      <footer className="border-t border-[#D4AF37]/15 px-5 py-8 text-center text-sm text-[#918A7E]">
        © {new Date().getFullYear()} {SITE.eventName} · Event by {SITE.organizer} · {SITE.city}
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display text-xl text-[#E6CA65] sm:text-2xl">{title}</h2>
      <div className="mt-3 space-y-3 text-base leading-[1.8] text-[#C8C2B7]">{children}</div>
    </section>
  );
}

export function PrivacyPage() {
  return (
    <LegalShell title="Privacy Policy" updated="23 August 2026" testId="privacy-page">
      <Section title="Who we are">
        <p>
          This website is the official online destination for {SITE.eventName} — {SITE.tagline} —
          held on {SITE.date} at {SITE.venue.name}, {SITE.venue.area}, {SITE.venue.city}. The event
          is organized by {SITE.organizer}.
        </p>
        <p>
          For any privacy question, contact us at{" "}
          <a
            href={`mailto:${SITE.contactEmail}`}
            className="font-semibold text-[#F3E5AB] underline decoration-[#D4AF37]/40 underline-offset-4 hover:text-[#E6CA65]"
          >
            {SITE.contactEmail}
          </a>
          .
        </p>
      </Section>

      <Section title="What this website collects">
        <p>
          This website is an informational and marketing page. It does not have accounts, forms
          that collect personal details, or any payment functionality.
        </p>
        <p>
          To understand how visitors find and use the page, we record anonymous usage events such
          as page views, button clicks, scroll depth and the campaign link (UTM parameters such as
          utm_source and utm_campaign) that brought you here. A random anonymous identifier is
          stored in your browser to count unique visitors. This information cannot identify you
          personally.
        </p>
      </Section>

      <Section title="Third-party services">
        <p>
          Tickets are sold exclusively through BookMyShow. When you choose to book, you leave this
          website and your purchase, payment and personal details are handled by BookMyShow under
          their own terms and privacy policy.
        </p>
        <p>
          When enabled by the organizer, this site may use Google Analytics, Google Ads and Meta
          measurement tools to evaluate advertising campaigns. These services may set their own
          cookies in line with their respective policies.
        </p>
        <p>
          Links to Instagram, Facebook, YouTube, WhatsApp, Google Maps and the artists' websites
          take you to third-party platforms governed by their own policies.
        </p>
      </Section>

      <Section title="Your choices">
        <p>
          You can clear or block cookies and local storage in your browser settings at any time.
          The site remains fully usable for reading event information.
        </p>
      </Section>

      <Section title="Changes to this policy">
        <p>
          If this policy changes, the updated version will be posted on this page with a revised
          date.
        </p>
      </Section>
    </LegalShell>
  );
}

export function TermsPage() {
  return (
    <LegalShell title="Terms & Conditions" updated="23 August 2026" testId="terms-page">
      <Section title="About this website">
        <p>
          This website is the official information and marketing page for {SITE.eventName} —{" "}
          {SITE.tagline} — organized by {SITE.organizer} and held on {SITE.date} at{" "}
          {SITE.venue.name}, {SITE.venue.area}, {SITE.venue.city}.
        </p>
      </Section>

      <Section title="Tickets">
        <p>
          All tickets are sold exclusively through BookMyShow, the event's ticketing platform.
          This website does not sell tickets, does not process payments and does not handle
          booking, refunds, exchanges or cancellations.
        </p>
        <p>
          Ticket prices, categories, availability, entry rules, refunds and all purchase-related
          terms are governed entirely by the official BookMyShow listing and BookMyShow's own
          terms of service.
        </p>
      </Section>

      <Section title="Event information">
        <p>
          Event details shown here — including the date, schedule, venue, artists and ticket
          prices — reflect the official announcements and the BookMyShow listing at the time of
          publication. The organizer may update event details; the BookMyShow listing is the
          authoritative source for ticket-related information.
        </p>
      </Section>

      <Section title="Intellectual property">
        <p>
          The {SITE.eventName} name, logo, poster artwork and content on this website belong to
          the event organizer. Please do not reuse them without written permission.
        </p>
      </Section>

      <Section title="Third-party links">
        <p>
          This website links to BookMyShow, Instagram, Facebook, YouTube, WhatsApp, Google Maps
          and artist websites. We are not responsible for the content or policies of those
          platforms.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          For questions about this website or the event, email{" "}
          <a
            href={`mailto:${SITE.contactEmail}`}
            className="font-semibold text-[#F3E5AB] underline decoration-[#D4AF37]/40 underline-offset-4 hover:text-[#E6CA65]"
          >
            {SITE.contactEmail}
          </a>
          .
        </p>
      </Section>
    </LegalShell>
  );
}
